import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import CategoryTabs from '@/components/feature/CategoryTabs';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { allVideos } from '@/mocks/videos';
import { categories, categoryInfo } from '@/mocks/categories';

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const currentCat = categories.find((c) => c.slug === slug);
  const info = categoryInfo[slug] || categoryInfo.all;

  let filteredVideos = slug === 'all' ? allVideos : allVideos.filter((v) => v.category === slug);

  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filteredVideos = filteredVideos.filter(
      (v) =>
        v.title.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q) ||
        v.tags.some((t) => t.toLowerCase().includes(q))
    );
  }

  const videoCount = filteredVideos.length;

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={
                searchQuery
                  ? [
                      { label: '视频分类', href: '/category/all' },
                      { label: currentCat?.name || '全部' },
                      { label: `搜索: ${searchQuery}` },
                    ]
                  : [{ label: currentCat?.name || '全部' }]
              }
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-100 mb-2">
                {searchQuery ? `搜索: "${searchQuery}"` : currentCat?.name || '全部视频'}
              </h1>
              <p className="text-sm text-foreground-500">
                {searchQuery
                  ? `找到 ${videoCount} 个相关结果`
                  : `共 ${videoCount} 部视频 · ${info.description.slice(0, 50)}...`}
              </p>
            </div>

            <div className="mb-8">
              <CategoryTabs />
            </div>

            {videoCount > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100 mb-4">
                  <i className="ri-search-line text-2xl text-foreground-500"></i>
                </div>
                <h3 className="text-lg font-medium text-foreground-300 mb-2">没有找到相关内容</h3>
                <p className="text-sm text-foreground-500">尝试更换搜索关键词或浏览其他分类</p>
              </div>
            )}

            {videoCount > 0 && (
              <div className="mt-10 text-center">
                <p className="text-sm text-foreground-500">
                  已展示全部 {videoCount} 个结果
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: info.title,
            description: info.description,
            url: `https://51video.com/category/${slug}`,
            isPartOf: { '@type': 'WebSite', name: '51视频', url: 'https://51video.com/' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51video.com/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentCat?.name || '全部',
                item: `https://51video.com/category/${slug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}